import { useEffect, useMemo, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { LoadModel } from '../client/ders'
import { useGetLoadsQuery } from '../client/ders'
import { combineSkip } from '../hooks/utils'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { LoadCacheRecord } from './storage/schema'
import { useGateValue } from '~/configs/statsig'

const LOADS_PAGE_SIZE = 1000

// Stable reference for the gated-off path (the common case for most orgs), so a
// fresh [] doesn't bust the manager's useMemo and re-render every accessor.
const EMPTY_LOADS: LoadModel[] = []

export type LoadsDataManagerContextValue = {
  data: LoadModel[]
  isLoading: boolean
  isFetching: boolean
}

function useLoadsQuery(enabled: boolean, options?: { skip?: boolean }) {
  const principalOrgId = usePrincipalOrgId()

  const { data, isLoading, isFetching } = useGetLoadsQuery(
    {
      orgIds: principalOrgId ? [principalOrgId] : undefined,
      orgDepth: 100,
      includeLocalManaged: true,
      page: 0,
      count: LOADS_PAGE_SIZE
    },
    combineSkip(!principalOrgId || !enabled, options)
  )

  return {
    loads: data?.data,
    isLoading,
    isFetching
  }
}

export default function useLoadsDataManager(service: IndexedDbService) {
  // Loads are behind the loads-in-ui gate. Like fuel tanks, this both skips the
  // fetch AND withholds any hydrated cache for orgs without the feature, so a
  // cache populated in a prior gated-on session can't leak loads into surfaces
  // that no longer check the gate while detail pages still 404.
  const hasLoadsInUi = useGateValue('loads-in-ui')
  const { loads, isLoading, isFetching } = useLoadsQuery(hasLoadsInUi)

  const data = useLoadsPersistence(service, loads, hasLoadsInUi)

  return useMemo<LoadsDataManagerContextValue>(
    () => ({
      data,
      isLoading,
      isFetching
    }),
    [data, isLoading, isFetching]
  )
}

function useLoadsPersistence(service: IndexedDbService, loads: LoadModel[] | undefined, enabled: boolean): LoadModel[] {
  const [hydratedLoads, setHydratedLoads] = useState<LoadModel[]>([])

  useEffect(() => {
    // Don't surface a previously-cached set when the feature is gated off.
    if (!enabled) {
      setHydratedLoads([])
      return
    }

    async function hydrateLoadsCache() {
      try {
        const records = await service.getAll('loads')
        setHydratedLoads(records.map((record) => record.payload))
      } catch (error) {
        logger.error('Failed to hydrate loads cache', error)
      }
    }

    void hydrateLoadsCache()
  }, [service, enabled])

  useEffect(() => {
    if (!loads) return

    const fetchedAt = Date.now()
    const records: LoadCacheRecord[] = loads.map((load) => ({
      id: load.id,
      payload: load,
      syncedAt: fetchedAt
    }))

    const replaceRecords = async () => {
      try {
        await service.replace('loads', records)
      } catch (error) {
        logger.error('Failed to persist loads snapshot', error)
      }
    }

    void replaceRecords()
  }, [loads, service])

  return enabled ? (loads ?? hydratedLoads) : EMPTY_LOADS
}
